export const GENERAL_EXTRA_SERVICE_LINE_STATUSES = [
  ['draft', 'Draft'],
  ['awaiting_approval', 'Awaiting approval'],
  ['approved', 'Approved'],
  ['declined', 'Declined'],
  ['completed', 'Completed'],
  ['not_completed', 'Not completed'],
  ['voided', 'Voided'],
]

export const GENERAL_EXTRA_SERVICE_LINE_SOURCES = [
  ['manual', 'Manual'],
  ['checklist_item', 'Checklist item'],
  ['finding', 'Finding'],
]

export const EXTRA_SERVICE_LINE_MODULES = [
  ['general', 'General'],
  ['suspension', 'Suspension'],
]

const BILLABLE_STATUSES = new Set(['approved', 'completed'])
const REPORT_STATUSES = new Set(['approved', 'declined', 'completed', 'not_completed'])
const CHECKLIST_SOURCE_RESULTS = {
  pre_service_inspection: new Set(['advisory', 'action_required']),
  job_checklist: new Set(['unable_to_complete', 'additional_work_required']),
}

function text(value) {
  return String(value || '').trim()
}

export function moneyNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? Math.max(0, Math.round(number * 100) / 100) : 0
}

export function normaliseExtraServiceLineStatus(value) {
  const key = text(value).toLowerCase()
  return GENERAL_EXTRA_SERVICE_LINE_STATUSES.some(([status]) => status === key) ? key : 'draft'
}

export function normaliseExtraServiceLineModuleKey(value) {
  return text(value).toLowerCase() === 'general' ? 'general' : 'suspension'
}

export function extraServiceLineModuleLabel(value) {
  return normaliseExtraServiceLineModuleKey(value) === 'general' ? 'General' : 'Suspension'
}

export function extraServiceLineStatusLabel(value) {
  const status = normaliseExtraServiceLineStatus(value)
  return GENERAL_EXTRA_SERVICE_LINE_STATUSES.find(([key]) => key === status)?.[1] || 'Draft'
}

export function extraServiceLineStatusTone(value) {
  const status = normaliseExtraServiceLineStatus(value)
  if (status === 'approved' || status === 'completed') return 'green'
  if (status === 'awaiting_approval') return 'orange'
  if (status === 'declined' || status === 'not_completed') return 'blue'
  if (status === 'voided') return 'muted'
  return 'muted'
}

export function isBillableExtraServiceLineStatus(value) {
  return BILLABLE_STATUSES.has(normaliseExtraServiceLineStatus(value))
}

export function isReportableExtraServiceLineStatus(value) {
  return REPORT_STATUSES.has(normaliseExtraServiceLineStatus(value))
}

export function canCreateExtraServiceLineFromChecklistItem(item = {}, moduleKey = 'general') {
  if (normaliseExtraServiceLineModuleKey(moduleKey) !== 'general') return false
  const stage = text(item.stage)
  const result = text(item.result)
  return Boolean(CHECKLIST_SOURCE_RESULTS[stage]?.has(result))
}

export function extraServiceLineNeedsCustomerExplanation(row = {}) {
  const status = normaliseExtraServiceLineStatus(row.status)
  return Boolean(
    row.approval_required !== false
    || row.customer_visible !== false
    || status === 'awaiting_approval'
    || isReportableExtraServiceLineStatus(status)
  )
}

export function canSendExtraServiceLineApproval(row = {}) {
  return Boolean(
    row
    && normaliseExtraServiceLineStatus(row.status) === 'draft'
    && row.approval_required !== false
    && text(row.service_id || row.service_snapshot?.id)
    && text(row.customer_explanation)
    && extraServiceLinePrice(row) > 0
  )
}

export function canEditExtraServiceLine(row = {}) {
  return normaliseExtraServiceLineStatus(row.status) === 'draft'
}

export function extraServiceLinePrice(row = {}) {
  return moneyNumber(row.line_amount ?? row.price_amount ?? row.service_snapshot?.base_price)
}

export function extraServiceLineBillableTotal(rows = []) {
  return rows
    .filter(row => isBillableExtraServiceLineStatus(row.status))
    .reduce((sum, row) => sum + extraServiceLinePrice(row), 0)
}

export function serviceLineSnapshotName(row = {}) {
  const fallback = `${extraServiceLineModuleLabel(row.module_key || row.service_snapshot?.module_key)} service`
  return text(row.service_snapshot?.name || row.service_name || row.name || row.title) || fallback
}

export function extraServiceLineJobScopeRows(rows = []) {
  return rows
    .filter(row => row && isBillableExtraServiceLineStatus(row.status))
    .map(row => {
      const name = serviceLineSnapshotName(row)
      const category = text(row.service_snapshot?.category_label || row.service_snapshot?.category || row.service_category)
      const key = `${name.toLowerCase()}|${category.toLowerCase()}`
      if (!name) return null
      return {
        id: text(row.id || row.service_id || key),
        name,
        category,
        amount: extraServiceLinePrice(row),
        status: normaliseExtraServiceLineStatus(row.status),
        status_label: extraServiceLineStatusLabel(row.status),
      }
    })
    .filter(Boolean)
}

export function extraServiceLineJobScopeSummary(rows = []) {
  const scopeRows = extraServiceLineJobScopeRows(rows)
  if (!scopeRows.length) return ''
  const names = scopeRows.map(row => row.name).filter(Boolean).join(', ')
  return `Approved extra service${scopeRows.length === 1 ? '' : 's'}: ${names}`
}

export function extraServiceLineCustomerSafeSnapshot(rows = []) {
  return rows
    .filter(row => row && row.customer_visible !== false)
    .filter(row => isReportableExtraServiceLineStatus(row.status))
    .map(row => ({
      id: text(row.id),
      source_type: text(row.source_type) || 'manual',
      service_id: text(row.service_id || row.service_snapshot?.id),
      service_name: serviceLineSnapshotName(row),
      service_category: text(row.service_snapshot?.category_label || row.service_snapshot?.category),
      service_description: text(row.service_snapshot?.description),
      customer_explanation: text(row.customer_explanation),
      status: normaliseExtraServiceLineStatus(row.status),
      status_label: extraServiceLineStatusLabel(row.status),
      customer_decision_at: text(row.customer_decision_at),
      customer_decision_note: text(row.customer_decision_note),
      technician_completion_comment: text(row.technician_completion_comment),
      evidence: customerSafeEvidence(row.evidence || row.linked_evidence || []),
    }))
    .filter(row => row.service_name && row.customer_explanation)
}

function customerSafeEvidence(rows = []) {
  return rows
    .map(row => row?.evidence || row)
    .filter(row => row && row.visibility === 'customer')
    .map(row => ({
      id: text(row.id),
      file_name: text(row.file_name),
      caption: text(row.caption),
      evidence_type: text(row.evidence_type),
      mime_type: text(row.mime_type),
      file_size: Number(row.file_size || 0),
      stage: text(row.stage),
    }))
    .filter(row => row.file_name || row.caption)
}

export function isMissingExtraServiceLineSchemaError(error) {
  const message = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].map(text).join(' ').toLowerCase()
  return message.includes('42p01')
    || message.includes('42703')
    || message.includes('42883')
    || message.includes('schema cache')
    || message.includes('could not find the table')
    || message.includes('could not find the function')
    || (message.includes('relation') && message.includes('does not exist'))
}
